const MAX_DIGITS = 8;

const numberFormatters = range(0, MAX_DIGITS + 1).map(
    (digits) => new Intl.NumberFormat(undefined, { maximumFractionDigits: digits }).format
);

// example: Jun 2018
export function shortMonthString(date: Date) {
    return date.toLocaleString(getBrowserLocale(), { month: "short" }) + " " + date.getFullYear();
}

// example: Jun 1, 2018
export function shortDateString(date: Date) {
    return (
        date.toLocaleString(getBrowserLocale(), { month: "short" }) + " " + date.getDate() + ", " + date.getFullYear()
    );
}

export function getBrowserLocale() {
    const userLocale = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;

    return userLocale || "en-US";
}

export function format(str: string, ...args: string[]) {
    for (let i = 0; i < args.length; i++) {
        const reg = new RegExp("\\{" + i + "\\}", "gm");
        str = str.replace(reg, args[i] ?? "");
    }
    return str;
}

export function formatNumber(value?: number, maxDigits = 0): string {
    if (typeof value !== "number" || isNaN(value)) return "";
    const formatter = numberFormatters[Math.min(maxDigits, MAX_DIGITS)];
    return formatter ? formatter(value) : "";
}

// Format a number based on total output length, including fraction
// For example, if maxLength = 5, then 0.1234 / 1.2345 / 12.345 / 123.45 / 1,234.5
export function formatNumberByLength(value: number, maxLength = MAX_DIGITS): string {
    const length = Math.min(maxLength, MAX_DIGITS);
    if (value < 1) return formatNumber(value, length - 1); // 0 is a digit
    // calculates the digits or places in the integer, for example 100 = 3
    const digits = (Math.log(value) * Math.LOG10E + 1) | 0;
    return formatNumber(value, length - Math.min(digits, length));
}

// Snippet adapted from https://www.joshwcomeau.com/snippets/javascript/range/
// Useful for pagination
export function range(start: number, end: number | undefined, step = 1) {
    const output: number[] = [];

    if (typeof end === "undefined") {
        end = start;
        start = 0;
    }

    for (let i = start; i < end; i += step) {
        output.push(i);
    }

    return output;
}

export function formatPackaging(packaging?: PartResultPackaging[]) {
    if (!Array.isArray(packaging) || packaging.length < 1) return "";
    return packaging
        .map((pkg) =>
            pkg.MinimumOrderQuantity
                ? `${pkg.PackageType} (${formatNumber(pkg.MinimumOrderQuantity)})`
                : pkg.PackageType
        )
        .join(", ");
}

/**
 * Formats a decimal value as a percentage
 * @param value - Decimal value (0-1) to format as percentage
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted percentage string or "N/A" if value is null/undefined
 */
export function formatPercentage(value?: number | null, decimals = 0): string {
    if (value === null || value === undefined) return "N/A";
    return `${(value * 100).toFixed(decimals)}%`;
}
